// VOBs — Operations Command Center (facelift, Day 52 campaign)
// Single payload from /api/vob/outstanding; facility/payer/tab/search scope
// applied client-side ONCE so every surface updates atomically.
// Brain preserved: VobDocs (upload/AI-extract/discrepancies), PATCH /complete,
// PATCH /issue (new). Mobile-friendly: tables overflowX, rail wraps under.
import React, { useState, useEffect, useMemo } from 'react';
import VobDocs from './components/VobDocs';
import { tokens as tk } from './theme';
import {
  Mail, Clock3, FileWarning, CheckCircle2, AlertTriangle, Search,
  ChevronDown, ChevronUp, Hourglass, Sparkles, Bell, FileCheck2, Inbox, Phone
} from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'https://tcio-api.onrender.com';
const DOT = '\u00B7';
const IC = { size: 16, strokeWidth: 1.75 };
const ICS = { size: 14, strokeWidth: 1.75 };

const fmtHrs = (h) => {
  const n = parseFloat(h);
  if (Number.isNaN(n)) return '-';
  if (n < 24) return Math.round(n) + 'h';
  return (n / 24).toFixed(1) + 'd';
};
const fmtDT = (d) => {
  if (!d) return '-';
  const s = String(d).slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const dt = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(d);
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};
const money = (v) => v == null ? null : '$' + Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 });

const chip = (bg, fg, br) => ({
  background: bg, color: fg, border: '1px solid ' + (br || bg),
  borderRadius: 6, fontSize: 11, fontWeight: 700, padding: '2px 8px',
  display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap'
});
const btn = (kind) => ({
  primary: { background: tk.ai, color: '#fff', border: 'none' },
  soft:    { background: tk.surfaceSoft, color: tk.ink, border: '1px solid ' + tk.border },
  warn:    { background: tk.warningBg, color: tk.warning, border: '1px solid ' + tk.warning },
  ok:      { background: tk.successBg, color: tk.success, border: '1px solid ' + tk.success },
}[kind] || {});
const btnBase = { borderRadius: tk.radiusControl || 8, padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 5 };

// Derived workflow state for a row — the single source of display truth.
const rowState = (r) => {
  if (r.in_house) return r.stage === 'complete' ? 'complete' : 'h_' + r.stage;   // in-house: requested, in_queue, on_call
  if (r.status === 'issue') return 'issue';
  if (['prelim_received', 'complete'].includes(r.status) && !r.has_document) {
    return r.status === 'complete' ? 'pdf_missing' : 'prelim';
  }
  if (r.status === 'sent') return 'waiting';
  if (r.status === 'prelim_received') return 'prelim';
  return 'complete';
};
const isPdfMissing = (r) => !r.in_house && ['prelim_received', 'complete'].includes(r.status) && !r.has_document;
const isDone = (r) => (r.in_house ? r.stage === 'complete' : r.status === 'complete');

export default function VOBs({ user }) {
  const headers = { Authorization: 'Bearer ' + user.token };
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [facilities, setFacilities] = useState([]);
  const [facility, setFacility] = useState('all');
  const [payer, setPayer] = useState('all');
  const [tab, setTab] = useState('open');
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [days, setDays] = useState(30);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(API + '/api/vob/outstanding?days=' + days, { headers });
      const j = await r.json();
      if (j.ok) {
        let st = {};
        try { const r2 = await fetch(API + '/api/vob/queue/stages', { headers }); const j2 = await r2.json(); if (j2.ok) st = j2.stages || {}; } catch (e) { /* the list still shows without stages */ }
        setRows(j.data.map(x => (st[x.id] ? { ...x, in_house: true, stage: st[x.id].stage, stage_with: st[x.id].with } : x)));
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);
  useEffect(() => {
    fetch(API + '/api/facilities', { headers })
      .then(r => r.json()).then(j => { if (j.ok) setFacilities(j.data || []); })
      .catch(() => {});
    // eslint-disable-next-line
  }, []);

  const facName = (id) => (facilities.find(f => f.id === id) || {}).org_name || '';

  // ONE scoped pipeline — facility + payer + search applied once.
  const scoped = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter(r =>
      (facility === 'all' || r.facility_id === facility) &&
      (payer === 'all' || (r.payer_name || '') === payer) &&
      (!needle ||
        (r.patient_name || '').toLowerCase().includes(needle) ||
        (r.payer_name || '').toLowerCase().includes(needle) ||
        (r.ref_code || '').toLowerCase().includes(needle))
    );
  }, [rows, facility, payer, q]);

  const payers = useMemo(() =>
    [...new Set(rows.map(r => r.payer_name).filter(Boolean))].sort(), [rows]);

  const buckets = useMemo(() => ({
    open:        scoped.filter(r => !isDone(r)),
    h_queue:     scoped.filter(r => r.in_house && ['requested', 'in_queue', 'received'].includes(r.stage)),
    h_call:      scoped.filter(r => r.in_house && r.stage === 'on_call'),
    waiting:     scoped.filter(r => r.status === 'sent' && !r.in_house),
    prelim:      scoped.filter(r => r.status === 'prelim_received' && !r.in_house),
    issue:       scoped.filter(r => r.status === 'issue'),
    pdf_missing: scoped.filter(isPdfMissing),
    complete:    scoped.filter(isDone),
    all:         scoped,
  }), [scoped]);

  const stats = useMemo(() => {
    const done = buckets.complete;
    const avg = done.length ? Math.round(done.reduce((n, r) => n + parseFloat(r.hours_out || 0), 0) / done.length) : null;
    const oldest = [...buckets.open].sort((a, b) => parseFloat(b.hours_out || 0) - parseFloat(a.hours_out || 0))[0] || null;
    return {
      open: buckets.open.length,
      waiting: buckets.waiting.length,
      over48: buckets.waiting.filter(r => parseFloat(r.hours_out) > 48).length,
      issues: buckets.issue.length,
      pdfMissing: buckets.pdf_missing.length,
      completed: done.length,
      avgHrs: avg,
      oldest,
    };
  }, [buckets]);

  const tabList = [
    ['open',        'Open',                buckets.open.length],
    ...(buckets.h_queue.length + buckets.h_call.length ? [['h_queue', 'In Queue', buckets.h_queue.length], ['h_call', 'On Call', buckets.h_call.length]] : []),
    ['waiting',     'Waiting on Biller',   buckets.waiting.length],
    ['prelim',      'Prelim In',           buckets.prelim.length],
    ['issue',       'Needs Follow-Up',     buckets.issue.length],
    ['pdf_missing', 'PDF Missing',         buckets.pdf_missing.length],
    ['complete',    'Completed',           buckets.complete.length],
    ['all',         'All',                 buckets.all.length],
  ];
  const visible = buckets[tab] || [];

  const markReceived = async (r) => {
    if (!window.confirm('Mark ' + (r.patient_name || 'this VOB') + ' as completed?\n\nUse this when the VOB came back but the Biller email was missed by the system.')) return;
    try {
      const res = await fetch(API + '/api/vob-requests/' + r.id + '/complete', { method: 'PATCH', headers });
      const j = await res.json();
      if (j.ok) load(); else alert(j.error || 'Failed');
    } catch (e) { alert('Failed'); }
  };
  const markIssue = async (r) => {
    const note = window.prompt('What did Biller say? One line (e.g. "Policy inactive until 8/31 - needs proof of payment").\n\nThis moves ' + (r.patient_name || 'this VOB') + ' to Needs Follow-Up.');
    if (note === null) return;
    try {
      const res = await fetch(API + '/api/vob-requests/' + r.id + '/issue', {
        method: 'PATCH', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ note })
      });
      const j = await res.json();
      if (j.ok) load(); else alert(j.error || 'Failed');
    } catch (e) { alert('Failed'); }
  };

  const [pdf, setPdf] = useState(null);
  // in-house requests: open the VOB itself, or the finished VOB as a PDF (there is no document to upload)
  const openVob = (r) => { try { localStorage.setItem('tcio_vob_open_lead', r.lead_id); } catch (e) { /* storage unavailable */ } window.dispatchEvent(new Event('tcio:open-vob')); };
  const openPdf = async (r) => {
    try { const res = await fetch(API + '/api/vob/workspace/' + r.lead_id + '/pdf?kind=summary', { headers });
      if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.error || 'The VOB could not be opened'); return; }
      const url = URL.createObjectURL(await res.blob()); setPdf(p => { if (p) URL.revokeObjectURL(p.url); return { url, name: r.patient_name }; });
    } catch (e) { alert('The VOB could not be opened'); }
  };
  const closePdf = () => setPdf(p => { if (p) URL.revokeObjectURL(p.url); return null; });

  // ---- render helpers ----
  const STATE_UI = {
    waiting:     { label: 'Waiting on Biller', bg: tk.warningBg, fg: '#9A6700', icon: Hourglass },
    prelim:      { label: 'Prelim In',         bg: tk.aiSoft,    fg: tk.blue700, icon: Inbox },
    issue:       { label: 'Needs Follow-Up',   bg: tk.dangerBg,  fg: tk.danger,  icon: AlertTriangle },
    pdf_missing: { label: 'PDF Missing',       bg: tk.purpleSoft,fg: tk.purple,  icon: FileWarning },
    complete:    { label: 'Completed',         bg: tk.successBg, fg: tk.success, icon: CheckCircle2 },
    h_requested: { label: 'Requested',         bg: tk.surfaceSoft, fg: tk.textSecondary, icon: Mail },
    h_in_queue:  { label: 'In Queue',          bg: tk.warningBg, fg: '#9A6700', icon: Hourglass },
    h_received:  { label: 'Received',          bg: tk.aiSoft,    fg: tk.blue700, icon: Hourglass },
    h_on_call:   { label: 'On Call',           bg: tk.aiSoft,    fg: tk.blue700, icon: Phone },
  };
  const stChip = (r) => {
    const s = STATE_UI[rowState(r)] || STATE_UI.waiting;
    const I = s.icon;
    return <span style={chip(s.bg, s.fg)}><I {...ICS} />{s.label}</span>;
  };
  const stuck = (r) => r.status === 'sent' && parseFloat(r.hours_out) > 48;

  const kpiCards = [
    { key: 'open',        label: 'Open',            val: stats.open,       icon: Mail,        accent: tk.blue700 },
    { key: 'waiting',     label: 'Waiting on Biller', val: stats.waiting,  icon: Hourglass,   accent: stats.over48 > 0 ? tk.warning : tk.blue700, sub: stats.over48 > 0 ? stats.over48 + ' over 48h' : null },
    { key: 'pdf_missing', label: 'PDF Missing',     val: stats.pdfMissing, icon: FileWarning, accent: tk.purple },
    { key: 'issue',       label: 'Needs Follow-Up', val: stats.issues,     icon: AlertTriangle, accent: stats.issues > 0 ? tk.danger : tk.textMuted },
    { key: 'complete',    label: 'Completed (' + days + 'd)', val: stats.completed, icon: CheckCircle2, accent: tk.success },
  ];

  const th = { textAlign: 'left', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: tk.textMuted, padding: '8px 12px', borderBottom: '1px solid ' + tk.border, whiteSpace: 'nowrap' };
  const td = { padding: '10px 12px', borderBottom: '1px solid ' + tk.borderSoft, fontSize: 13, color: tk.ink, verticalAlign: 'top' };

  const Trail = ({ r }) => {
    const steps = r.in_house ? [
      ['Requested', true, fmtDT(r.requested_at)],
      ['In queue', ['in_queue', 'received', 'on_call', 'complete'].includes(r.stage), null],
      ['Received' + (r.stage_with ? ' ' + DOT + ' ' + r.stage_with : ''), ['received', 'on_call', 'complete'].includes(r.stage), null],
      ['On call' + (r.stage_with ? ' ' + DOT + ' ' + r.stage_with : ''), ['on_call', 'complete'].includes(r.stage), null],
      ['Completed', r.stage === 'complete', fmtDT(r.full_received_at)],
    ] : [
      ['Requested', true, fmtDT(r.requested_at)],
      ['Prelim VOB received', !!r.prelim_received_at, fmtDT(r.prelim_received_at)],
      ['Full VOB received', !!r.full_received_at, fmtDT(r.full_received_at)],
      ['Source PDF on file', !!r.has_document, null],
    ];
    return (
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        {steps.map(([label, done, when]) => (
          <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: done ? tk.ink : tk.textMuted, fontWeight: done ? 600 : 500 }}>
            {done ? <CheckCircle2 {...ICS} color={tk.success} /> : <Clock3 {...ICS} color={tk.textMuted} />}
            {label}{when && done ? ' ' + DOT + ' ' + when : ''}
          </span>
        ))}
      </div>
    );
  };

  const hasSmart = (r) => [r.smart_vob_dtx, r.smart_vob_rtc, r.smart_vob_php, r.smart_vob_iop].some(v => v != null);

  const Action = ({ r }) => {
    const st = rowState(r);
    if (r.in_house) return (
      <button style={{ ...btnBase, ...btn('soft') }} onClick={() => (r.stage === 'complete' ? openPdf(r) : openVob(r))}>
        <FileCheck2 {...ICS} />{r.stage === 'complete' ? 'View VOB' : 'Open'}
      </button>
    );
    if (st === 'waiting' || st === 'issue') return (
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button style={{ ...btnBase, ...btn('ok') }} onClick={() => markReceived(r)}><CheckCircle2 {...ICS} />Received</button>
        {st === 'waiting' && <button style={{ ...btnBase, ...btn('warn') }} onClick={() => markIssue(r)}><AlertTriangle {...ICS} />Issue</button>}
      </div>
    );
    if (st === 'pdf_missing') return (
      <button style={{ ...btnBase, background: tk.purple, color: '#fff', border: 'none' }}
        onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
        <FileWarning {...ICS} />Upload PDF
      </button>
    );
    return (
      <button style={{ ...btnBase, ...btn('soft') }} onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
        <FileCheck2 {...ICS} />Docs
      </button>
    );
  };

  return (
    <div style={{ padding: '24px 28px', fontFamily: tk.font, maxWidth: 1500 }}>
      {pdf && (
        <div onClick={closePdf} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: tk.surface, border: '1px solid ' + tk.border, borderRadius: tk.radiusCard, width: 'min(940px, 100%)', height: 'min(92vh, 1200px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid ' + tk.border }}>
              <div style={{ flex: 1, minWidth: 0, fontWeight: 700, fontSize: 15, color: tk.ink }}>VOB: {pdf.name || ''}</div>
              <a href={pdf.url} download={'VOB_' + String(pdf.name || 'client').replace(/\W+/g, '_') + '.pdf'} style={{ ...btnBase, ...btn('soft'), textDecoration: 'none' }}>Download</a>
              <button onClick={closePdf} style={{ ...btnBase, ...btn('primary') }}>Close</button>
            </div>
            <iframe title="VOB" src={pdf.url} style={{ flex: 1, width: '100%', border: 'none', background: tk.surfaceSoft }} />
          </div>
        </div>
      )}
      {/* Header + controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
        <div>
          <h1 style={{ fontSize: 23, fontWeight: 800, color: tk.ink, margin: 0 }}>Verification of Benefits</h1>
          <p style={{ color: tk.textSecondary, margin: '3px 0 0', fontSize: 13.5 }}>
            Every VOB request, what TCIO has already automated, and what needs a human next.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={facility} onChange={e => setFacility(e.target.value)}
            style={{ ...btnBase, ...btn('soft'), appearance: 'auto', fontWeight: 600 }}>
            <option value="all">All Facilities</option>
            {facilities.map(f => <option key={f.id} value={f.id}>{f.org_name}</option>)}
          </select>
          <select value={payer} onChange={e => setPayer(e.target.value)}
            style={{ ...btnBase, ...btn('soft'), appearance: 'auto', fontWeight: 600, maxWidth: 190 }}>
            <option value="all">All Payers</option>
            {payers.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={days} onChange={e => setDays(+e.target.value)}
            style={{ ...btnBase, ...btn('soft'), appearance: 'auto', fontWeight: 600 }}>
            <option value={30}>Last 30 Days</option>
            <option value={60}>Last 60 Days</option>
            <option value={120}>Last 120 Days</option>
          </select>
        </div>
      </div>

      {/* KPI strip — cards are filters */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        {kpiCards.map(k => {
          const I = k.icon; const active = tab === k.key;
          return (
            <div key={k.key} onClick={() => setTab(active ? 'open' : k.key)}
              style={{ background: tk.surface, border: '1px solid ' + (active ? tk.ai : tk.border), borderRadius: tk.radiusCard, padding: '12px 16px', minWidth: 150, cursor: 'pointer', boxShadow: active ? '0 0 0 1px ' + tk.ai : tk.shadow, flex: '1 1 150px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10.5, color: tk.textMuted, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em' }}>
                <I {...ICS} color={k.accent} />{k.label}
              </div>
              <div style={{ fontSize: 24, fontWeight: 800, color: k.accent, fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>{k.val}</div>
              {k.sub && <div style={{ fontSize: 11, color: tk.warning, fontWeight: 600 }}>{k.sub}</div>}
            </div>
          );
        })}
        <div style={{ background: tk.surface, border: '1px solid ' + tk.border, borderRadius: tk.radiusCard, padding: '12px 16px', minWidth: 160, flex: '1 1 160px' }}>
          <div style={{ fontSize: 10.5, color: tk.textMuted, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em' }}>Avg Turnaround</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: tk.blue700, fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>{stats.avgHrs != null ? fmtHrs(stats.avgHrs) : '-'}</div>
          {stats.oldest && (
            <div style={{ fontSize: 11, color: tk.textSecondary, cursor: 'pointer' }} onClick={() => { setTab('open'); setQ(stats.oldest.patient_name || ''); }}>
              Oldest open: <b>{fmtHrs(stats.oldest.hours_out)}</b> {DOT} {stats.oldest.patient_name}
            </div>
          )}
        </div>
      </div>

      {/* TCIO Insight — only when real */}
      {stats.pdfMissing > 0 && (
        <div style={{ background: tk.purpleSoft, border: '1px solid ' + tk.purple, borderRadius: tk.radiusCard, padding: '12px 16px', marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <Sparkles {...IC} color={tk.purple} />
            <div>
              <span style={{ fontSize: 10.5, fontWeight: 800, color: tk.purple, letterSpacing: '0.07em' }}>TCIO INSIGHT</span>
              <div style={{ fontSize: 13, color: tk.ink, fontWeight: 600 }}>
                {stats.pdfMissing} VOB{stats.pdfMissing > 1 ? 's have' : ' has'} returned with data captured, but the source PDF still needs to be uploaded.
              </div>
            </div>
          </div>
          <button style={{ ...btnBase, background: tk.purple, color: '#fff', border: 'none' }} onClick={() => setTab('pdf_missing')}>Upload Documents</button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Main column */}
        <div style={{ flex: '1 1 640px', minWidth: 0 }}>
          {/* Tabs */}
          <div style={{ display: 'flex', gap: 2, borderBottom: '2px solid ' + tk.border, marginBottom: 10, overflowX: 'auto' }}>
            {tabList.map(([key, label, n]) => (
              <button key={key} onClick={() => setTab(key)}
                style={{ background: 'none', border: 'none', borderBottom: '2px solid ' + (tab === key ? tk.ai : 'transparent'), marginBottom: -2, padding: '8px 12px', fontSize: 13, fontWeight: 700, color: tab === key ? tk.ai : tk.textSecondary, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {label} <span style={{ fontVariantNumeric: 'tabular-nums', color: tab === key ? tk.ai : tk.textMuted }}>{n}</span>
              </button>
            ))}
          </div>

          {/* Search */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: tk.surface, border: '1px solid ' + tk.border, borderRadius: tk.radiusControl, padding: '7px 12px', marginBottom: 10, maxWidth: 380 }}>
            <Search {...ICS} color={tk.textMuted} />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search patient, payer, or ref code..."
              style={{ border: 'none', outline: 'none', fontSize: 13, flex: 1, background: 'transparent', color: tk.ink, fontFamily: tk.font }} />
          </div>

          {loading ? <div style={{ color: tk.textMuted, padding: 30 }}>Loading...</div> : (
            <div style={{ background: tk.surface, border: '1px solid ' + tk.border, borderRadius: tk.radiusCard, overflowX: 'auto' }}>
              {visible.length === 0 && (
                <div style={{ padding: 24, color: tk.success, fontWeight: 600, fontSize: 13.5 }}>Nothing here. All caught up.</div>
              )}
              {visible.length > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
                  <thead><tr>
                    <th style={th}>Patient</th><th style={th}>Payer</th><th style={th}>Status</th>
                    <th style={th}>Age</th><th style={th}>Requested</th><th style={th}>Action</th>
                  </tr></thead>
                  <tbody>
                    {visible.map(r => (
                      <React.Fragment key={r.id}>
                        <tr style={{ background: stuck(r) ? tk.warningBg : 'transparent' }}>
                          <td style={{ ...td, cursor: 'pointer' }} onClick={() => ((r.in_house && r.stage === 'complete') ? openPdf(r) : setExpanded(expanded === r.id ? null : r.id))}>
                            <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                              {r.patient_name || 'Unknown'}
                              {expanded === r.id ? <ChevronUp {...ICS} color={tk.textMuted} /> : <ChevronDown {...ICS} color={tk.textMuted} />}
                            </div>
                            <div style={{ fontSize: 11.5, color: tk.textMuted }}>{r.ref_code}{facName(r.facility_id) ? ' ' + DOT + ' ' + facName(r.facility_id) : ''}</div>
                          </td>
                          <td style={td}>
                            <div style={{ fontWeight: 600 }}>{r.payer_name || 'Unknown payer'}</div>
                            <div style={{ fontSize: 11.5, color: tk.textMuted }}>by {(r.rep_first || '') + ' ' + (r.rep_last || '')}</div>
                          </td>
                          <td style={td}>
                            {stChip(r)}
                            {r.status === 'issue' && r.issue_note && <div style={{ fontSize: 11.5, color: tk.danger, marginTop: 3, maxWidth: 220 }}>{r.issue_note}</div>}
                          </td>
                          <td style={{ ...td, fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: stuck(r) ? tk.warning : tk.ink }}>{fmtHrs(r.hours_out)}</td>
                          <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDT(r.requested_at)}</td>
                          <td style={td}><Action r={r} /></td>
                        </tr>
                        {expanded === r.id && (
                          <tr><td colSpan={6} style={{ ...td, background: tk.surfaceSoft, padding: '14px 16px' }}>
                            <Trail r={r} />
                            {hasSmart(r) && (
                              <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                <span style={{ fontSize: 10.5, fontWeight: 800, color: tk.teal, letterSpacing: '0.06em' }}>SMART VOB EST. ALLOWED</span>
                                {[['DTX', r.smart_vob_dtx], ['RTC', r.smart_vob_rtc], ['PHP', r.smart_vob_php], ['IOP', r.smart_vob_iop]]
                                  .filter(([, v]) => v != null)
                                  .map(([loc, v]) => <span key={loc} style={chip(tk.tealSoft, tk.teal)}>{loc} {money(v)}/day</span>)}
                                <span style={{ fontSize: 11, color: tk.textMuted }}>Biller estimate from aggregate data {DOT} not a payment guarantee</span>
                              </div>
                            )}
                            {!r.in_house && <div style={{ marginTop: 10 }}><VobDocs leadId={r.lead_id} user={user} compact /></div>}
                          </td></tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

        {/* Needs Attention rail */}
        <div style={{ flex: '0 1 270px', minWidth: 240 }}>
          <div style={{ background: tk.surface, border: '1px solid ' + tk.border, borderRadius: tk.radiusCard, padding: '14px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 800, color: tk.ink, letterSpacing: '0.06em', marginBottom: 10 }}>
              <Bell {...ICS} color={tk.warning} />NEEDS ATTENTION
            </div>
            {[
              [stats.pdfMissing, 'PDF Missing', 'Data captured ' + DOT + ' upload the source document', 'pdf_missing', tk.purple],
              [stats.over48, 'Waiting on Biller over 48h', 'Consider a follow-up', 'waiting', tk.warning],
              [stats.issues, 'Needs Follow-Up', 'Biller flagged an issue', 'issue', tk.danger],
            ].filter(([n]) => n > 0).map(([n, label, sub, tkey, color]) => (
              <div key={label} onClick={() => setTab(tkey)}
                style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid ' + tk.borderSoft, cursor: 'pointer' }}>
                <span style={{ fontSize: 19, fontWeight: 800, color, fontVariantNumeric: 'tabular-nums', minWidth: 26 }}>{n}</span>
                <span><div style={{ fontSize: 12.5, fontWeight: 700, color: tk.ink }}>{label}</div>
                <div style={{ fontSize: 11.5, color: tk.textSecondary }}>{sub}</div></span>
              </div>
            ))}
            {stats.pdfMissing === 0 && stats.over48 === 0 && stats.issues === 0 && (
              <div style={{ fontSize: 12.5, color: tk.success, fontWeight: 600 }}>All clear.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
